import React, { useEffect, useState } from "react";
import { patch } from "../lib/api";
import { diffPatch, dueText, formatDay, humanize, relativeDay } from "../lib/format";
import { Badge, Dot, Field, Input, Select, Textarea, dueDotColor } from "../ui";
import { useShell } from "../shell/context";

// PATCH helper for a detail page: sends only changed keys, keeps the page's
// entity in sync with the response, and reports errors as toasts.
export function usePatcher(path, entity, setEntity) {
    const { toast, bump, noteWarnings } = useShell();
    return async (changes, { quiet } = {}) => {
        const body = diffPatch(entity, changes, Object.keys(changes));
        if (Object.keys(body).length === 0) return true;
        try {
            const res = await patch(path, body);
            setEntity((e) => ({ ...e, ...res }));
            if (!quiet) toast("Saved.");
            noteWarnings(res);
            bump();
            return true;
        } catch (e) {
            toast(e.message, { error: true });
            return false;
        }
    };
}

// Text input that saves on blur (one PATCH per edit, not per keystroke:
// every PATCH writes an audit row).
export function AutoField({ label, field, entity, save, type = "text", placeholder, multiline, rows = 4, options, hint }) {
    const original = entity[field] === null || entity[field] === undefined ? "" : String(entity[field]);
    const [value, setValue] = useState(original);
    useEffect(() => setValue(original), [original]);
    const commit = (v = value) => {
        const next = typeof v === "string" ? v.trim() : v;
        if (next === original) return;
        save({ [field]: type === "number" ? (next === "" ? null : Number(next)) : next }).then((ok) => !ok && setValue(original));
    };
    let control;
    if (options) {
        control = (
            <Select
                value={value}
                placeholder="Not set"
                options={options}
                onChange={(e) => {
                    setValue(e.target.value);
                    commit(e.target.value);
                }}
            />
        );
    } else if (multiline) {
        control = <Textarea rows={rows} value={value} placeholder={placeholder} onChange={(e) => setValue(e.target.value)} onBlur={() => commit()} />;
    } else {
        control = (
            <Input
                type={type}
                value={value}
                placeholder={placeholder}
                onChange={(e) => setValue(e.target.value)}
                onBlur={() => commit()}
                onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            />
        );
    }
    return (
        <Field label={label} hint={hint}>
            {control}
        </Field>
    );
}

export function InlineName({ entity, save }) {
    const [value, setValue] = useState(entity.name);
    useEffect(() => setValue(entity.name), [entity.name]);
    return (
        <input
            className="hz-inline-name"
            value={value}
            aria-label="Name"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            onBlur={() => {
                if (!value.trim()) return setValue(entity.name);
                if (value.trim() !== entity.name) save({ name: value.trim() });
            }}
        />
    );
}

// Small number input for cadence days; empty clears.
export function DaysInput({ value, placeholder, onSave, width = 150 }) {
    const original = value ? String(value) : "";
    const [v, setV] = useState(original);
    useEffect(() => setV(original), [original]);
    return (
        <span className="hz-row" style={{ gap: 6 }}>
            <Input
                small
                type="number"
                min={1}
                max={3650}
                value={v}
                placeholder={placeholder}
                style={{ width }}
                onChange={(e) => setV(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                onBlur={() => {
                    if (v === original) return;
                    onSave(v === "" ? null : Number(v)).then((ok) => !ok && setV(original));
                }}
            />
        </span>
    );
}

// Due summary block for the detail header.
export function DueSummary({ entity, notTrackedText }) {
    const status = entity.due_status;
    const color = dueDotColor(status);
    const label = status === "overdue" ? "Overdue" : status === "due_soon" ? "Due soon" : status === "ok" ? "OK" : "Not kept in touch";
    return (
        <div>
            <div className={`hz-row hz-status-${color}`} style={{ gap: 6, fontWeight: 600 }}>
                <Dot color={color} small />
                {label}
                {status && <span className="hz-small" style={{ fontWeight: 400 }}>· {dueText(entity.due_in_days)}</span>}
            </div>
            <div className="hz-small" style={{ color: "var(--hz-gray-700)" }}>
                {entity.last_touch_at ? `Last touch ${formatDay(entity.last_touch_at, { short: true })} (${relativeDay(entity.last_touch_at)})` : "No touch logged yet"}
            </div>
            <div className="hz-small hz-muted">
                {status ? `Next due ${formatDay(entity.next_due_date, { short: true })} · every ${entity.effective_cadence_days} days` : notTrackedText}
            </div>
        </div>
    );
}

export function SourceBadge({ source }) {
    if (!source) return null;
    return <Badge color={source === "manual" ? "primary" : "secondary"} title={source === "manual" ? "Entered by hand; imports never overwrite it" : "From an import"}>{humanize(source)}</Badge>;
}
